/**
 * Module colour system: muted, confident colours with clear semantic meaning.
 * Header, active button, focus ring, and accent badges derive from the same base per module.
 * Works in light and dark mode. Single source for all header/nav styles.
 *
 * When a module is active:
 * - Header background = that module's Header colour
 * - Primary/active buttons = Button Active
 * - Focus rings = Button Active
 * - Pills/counters = Accent Badge
 */

export const MODULE_COLORS = {
  warehouse: {
    base: '#2F6F6A',
    header: '#245B57',
    buttonActive: '#3E8E87',
    accentBadge: '#BFE5E2',
    textOnColor: '#FFFFFF',
    label: 'Products',
    buttonLabel: 'Products',
    badgeLabel: 'PRODUCTS'
  },
  jobs: {
    base: '#2563EB',
    header: '#1E4ED8',
    buttonActive: '#3B82F6',
    accentBadge: '#DBEAFE',
    textOnColor: '#FFFFFF',
    label: '3D',
    buttonLabel: '3D',
    badgeLabel: '3D'
  },
  po: {
    base: '#6B7280',
    header: '#4B5563',
    buttonActive: '#9CA3AF',
    /** Orange nav underline so it stands out on dark header */
    indicator: '#F97316',
    accentBadge: '#E5E7EB',
    textOnColor: '#FFFFFF',
    label: 'Manage Incoming Stock',
    buttonLabel: 'Incoming Stock',
    badgeLabel: 'INCOMING STOCK'
  },
  'po-planning': {
    base: '#4F46E5',
    header: '#4338CA',
    buttonActive: '#6366F1',
    indicator: '#A5B4FC',
    accentBadge: '#E0E7FF',
    textOnColor: '#FFFFFF',
    label: 'Inventory Planner',
    /** Header is tight with PriceMole added — short button labels, full names elsewhere */
    buttonLabel: 'Planner',
    badgeLabel: 'INVENTORY PLANNER'
  },
  preorders: {
    base: '#7C3AED',
    header: '#6D28D9',
    buttonActive: '#8B5CF6',
    accentBadge: '#EDE9FE',
    textOnColor: '#FFFFFF',
    label: 'Preorders',
    buttonLabel: 'Preorders',
    badgeLabel: 'PREORDERS'
  },
  ra: {
    base: '#D97706',
    header: '#B45309',
    buttonActive: '#F59E0B',
    /** Red nav underline for Repairs */
    indicator: '#EF4444',
    accentBadge: '#FEF3C7',
    textOnColor: '#1F2937',
    label: 'Returns & Repairs',
    buttonLabel: 'Returns & Repairs',
    badgeLabel: 'RETURNS & REPAIRS'
  },
  'new-products': {
    base: '#DB2777',
    header: '#BE185D',
    buttonActive: '#EC4899',
    indicator: '#F472B6',
    accentBadge: '#FCE7F3',
    textOnColor: '#FFFFFF',
    label: 'New Products',
    buttonLabel: 'New Products',
    badgeLabel: 'NEW PRODUCTS'
  },
  picking: {
    base: '#059669',
    header: '#047857',
    buttonActive: '#10B981',
    indicator: '#047857',
    accentBadge: '#D1FAE5',
    textOnColor: '#FFFFFF',
    label: 'Picking',
    buttonLabel: 'Picking',
    badgeLabel: 'PICKING'
  },
  /** Food expiry dashboard (/expiry): Products teal, amber underline for "due soon". */
  expiry: {
    base: '#2F6F6A',
    header: '#245B57',
    buttonActive: '#3E8E87',
    indicator: '#F59E0B',
    accentBadge: '#BFE5E2',
    textOnColor: '#FFFFFF',
    label: 'Expiry',
    buttonLabel: 'Expiry',
    badgeLabel: 'EXPIRY'
  },
  /** CEO master watchlist (/ceo, 2026-10-23): slate header with an amber underline, admin-only by default. */
  ceo: { base: '#1F2937', header: '#0F172A', buttonActive: '#334155', indicator: '#F59E0B', accentBadge: '#E2E8F0', textOnColor: '#FFFFFF', label: 'CEO', buttonLabel: 'CEO', badgeLabel: 'CEO' },
  'backup-restore': { base: '#365587', header: '#253c61', buttonActive: '#496fab', indicator: '#a8c7fa', accentBadge: '#dbeafe', textOnColor: '#FFFFFF', label: 'Backup & Restore', buttonLabel: 'Backups', badgeLabel: 'BACKUP & RESTORE' },
  map: {
    base: '#0F766E',
    header: '#0D5C56',
    buttonActive: '#14B8A6',
    indicator: '#2DD4BF',
    accentBadge: '#CCFBF1',
    textOnColor: '#FFFFFF',
    label: 'Warehouse Map',
    buttonLabel: 'MAP',
    badgeLabel: 'MAP'
  },
  'picking-supervisor': {
    base: '#059669',
    header: '#047857',
    buttonActive: '#10B981',
    indicator: '#047857',
    accentBadge: '#D1FAE5',
    textOnColor: '#FFFFFF',
    label: 'Picking Supervisor',
    buttonLabel: 'Picking',
    badgeLabel: 'PICKING'
  },
  /** Packing-bench navy — distinct from pickup blue and picking green. */
  ship: {
    base: '#0F4C81',
    header: '#0A365C',
    buttonActive: '#1565A8',
    indicator: '#7EB6D9',
    accentBadge: '#D6E8F5',
    textOnColor: '#FFFFFF',
    label: 'Ship',
    buttonLabel: 'Ship',
    badgeLabel: 'SHIP'
  },
  /** Counter blue — distinct from picking green so the tablet is unmistakable. */
  'pick-from-store': {
    base: '#1D4ED8',
    header: '#1E3A8A',
    buttonActive: '#3B82F6',
    indicator: '#93C5FD',
    accentBadge: '#DBEAFE',
    textOnColor: '#FFFFFF',
    label: 'Pick From Store',
    /**
     * Kept to one short word on purpose: the header grid already over-subscribes
     * its row (the nav and utils rows overlap by ~77px at 1512 before this
     * module existed), so a long label here visibly collides with the tools.
     */
    buttonLabel: 'Pickups',
    badgeLabel: 'PICKUP BAY'
  },
  /** Delivery-van orange — warmer than Repairs amber, unmistakable on the road. */
  'local-deliveries': {
    base: '#C2410C',
    header: '#9A3412',
    buttonActive: '#EA580C',
    indicator: '#FDBA74',
    accentBadge: '#FFEDD5',
    textOnColor: '#FFFFFF',
    label: 'Local Deliveries',
    /** Lives in HEADER_TOOL_KEYS (after Roast Stocktake), not the nav row. */
    buttonLabel: 'Local Deliveries',
    badgeLabel: 'DELIVERIES'
  },
  tags: {
    base: '#5B2247',
    header: '#3C1A2E',
    buttonActive: '#7A3560',
    indicator: '#D977A8',
    accentBadge: '#F5D0E5',
    textOnColor: '#FFFFFF',
    label: 'Product Tags',
    buttonLabel: 'Tags',
    badgeLabel: 'PRODUCT TAGS'
  },
  pricemole: {
    /** Espresso brown — the mole that watches competitor prices */
    base: '#7C4A21',
    header: '#5F3818',
    buttonActive: '#9A6231',
    indicator: '#E0A868',
    accentBadge: '#F3DFC9',
    textOnColor: '#FFFFFF',
    label: 'PriceMole',
    buttonLabel: 'PriceMole',
    badgeLabel: 'PRICEMOLE'
  },
  'coffee-stocktake': {
    /** Roasted-bean green — weekly coffee stocktake into Qoblex */
    base: '#2F5D50',
    header: '#1F4137',
    buttonActive: '#3E7A68',
    indicator: '#7FCBAF',
    accentBadge: '#CFE9DE',
    textOnColor: '#FFFFFF',
    label: 'Roast Stocktake',
    buttonLabel: 'Roast Stocktake',
    badgeLabel: 'ROAST STOCKTAKE'
  }
};

/** Browser tab / PWA branding */
export const APP_NAME = 'Phaser Warehouse App';

/** Display name per moduleView (App.js routing key) */
const MODULE_VIEW_TITLE_LABELS = {
  home: MODULE_COLORS.warehouse.label,
  ceo: MODULE_COLORS.ceo.label,
  jobs: MODULE_COLORS.jobs.label,
  ra: MODULE_COLORS.ra.label,
  po: MODULE_COLORS.po.buttonLabel,
  'po-planning': MODULE_COLORS['po-planning'].label,
  preorders: MODULE_COLORS.preorders.label,
  picking: MODULE_COLORS.picking.label,
  'picking-supervisor': MODULE_COLORS['picking-supervisor'].label,
  ship: MODULE_COLORS.ship.label,
  'pick-from-store': MODULE_COLORS['pick-from-store'].label,
  'local-deliveries': MODULE_COLORS['local-deliveries'].label,
  tags: MODULE_COLORS.tags.label,
  'new-products': MODULE_COLORS['new-products'].label,
  'backup-restore': MODULE_COLORS['backup-restore'].label,
  expiry: MODULE_COLORS.expiry.label,
  map: MODULE_COLORS.map.label,
  pricemole: MODULE_COLORS.pricemole.label,
  'coffee-stocktake': MODULE_COLORS['coffee-stocktake'].label
};

/** Build document.title for the active module view */
export function getDocumentTitle(moduleView, _jobsHubActiveTab = 0) {
  const view = moduleView;
  const moduleLabel = MODULE_VIEW_TITLE_LABELS[view] ?? APP_NAME;
  return `${APP_NAME} – ${moduleLabel}`;
}

/** Backwards compat: header colour for home is warehouse */
export const DEFAULT_HEADER_COLOR = MODULE_COLORS.warehouse.header;

/** Header dimensions — same everywhere */
export const HEADER_HEIGHT_PX = 56;
export const HEADER_PADDING = '12px 16px';

  /** Nav (Rhys 2026-09-28): Picking, Products, Incoming Stock, Preorders, Ship, Map, Pickups, then 3D + Repairs
   * (spacer inserted in App before jobs/ra). Ship shows only to users with the `ship` module permission.
   * Expiry (food expiry dashboard, 2026-10-07) sits beside Products, gated by the same `warehouse` module.
   * CEO (2026-10-23) comes first; only users holding the `ceo` grant see it. */
export const NAV_KEYS = ['ceo', 'picking-supervisor', 'warehouse', 'po', 'preorders', 'ship', 'map', 'expiry', 'pick-from-store', 'jobs', 'ra'];

/** Keys rendered after the left-nav spacer (job hubs) */
export const NAV_JOB_HUB_KEYS = ['jobs', 'ra'];

/** Compact header tools (right side, left of Product Tags) */
export const HEADER_TOOL_KEYS = ['po-planning', 'new-products', 'pricemole', 'coffee-stocktake', 'local-deliveries', 'backup-restore'];

export function getNavLabel(key) {
  return MODULE_COLORS[key]?.buttonLabel ?? key;
}

/** Hex to rgba for inactive/muted states */
export function hexToRgba(hex, alpha) {
  const m = hex.slice(1).match(/(.{2})/g);
  if (!m) return hex;
  const [r, g, b] = m.map(x => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Text colour on header/module (Returns uses dark text) */
export function getHeaderTextColor(moduleKey) {
  return MODULE_COLORS[moduleKey]?.textOnColor ?? '#FFFFFF';
}

/** Single source for header bar container style. Three columns: nav | dev label | utils. */
export function getHeaderContainerStyle(overrides = {}) {
  return {
    minHeight: HEADER_HEIGHT_PX,
    padding: HEADER_PADDING,
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: '16px',
    position: 'relative',
    ...overrides
  };
}

/** Focus ring colour for nav buttons (Button Active) — set as CSS var --module-focus-ring on the header container */
export function getModuleFocusRingColor(moduleKey) {
  return MODULE_COLORS[moduleKey]?.buttonActive ?? MODULE_COLORS.warehouse.buttonActive;
}

/** Hover background for nav button */
export function getHeaderNavButtonHoverBg(moduleKey) {
  const c = MODULE_COLORS[moduleKey];
  if (!c) return hexToRgba(DEFAULT_HEADER_COLOR, 0.7);
  return hexToRgba(c.buttonActive, 0.85);
}

/**
 * Section accent colour for nav active indicator (e.g. bottom border).
 * Uses optional `indicator` when set (e.g. PO uses light grey so underline stands out on dark header).
 */
export function getSectionAccentColor(moduleKey) {
  const c = MODULE_COLORS[moduleKey];
  return c?.indicator ?? c?.buttonActive ?? MODULE_COLORS.warehouse.buttonActive;
}

/**
 * Nav button style: use class "module-header-nav-btn" + "active" for styling.
 * This returns an empty object so CSS variables (--nav-tab-*) control appearance
 * and all tabs (including Repairs) share the same text colour for consistency.
 */
export function getHeaderNavButtonStyle(_isActive, _moduleKey) {
  return {};
}

/** Center title style in header. Pass active module key for correct text colour (e.g. Returns = dark). */
export function getHeaderTitleStyle(moduleKey) {
  const color = moduleKey ? getHeaderTextColor(moduleKey) : '#FFFFFF';
  return {
    fontSize: 20,
    fontWeight: 600,
    color,
    textAlign: 'center',
    justifySelf: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%'
  };
}

/** Header colour for a module (for backdrop, etc.) */
export function getModuleColor(key) {
  return MODULE_COLORS[key]?.header ?? DEFAULT_HEADER_COLOR;
}

/** Base colour for module badge (centre title lockup) */
export function getModuleBaseColor(key) {
  return MODULE_COLORS[key]?.base ?? MODULE_COLORS.warehouse.base;
}

/** Short label for centre module badge (e.g. "3D", "REPAIRS") */
export function getModuleBadgeLabel(key) {
  return MODULE_COLORS[key]?.badgeLabel ?? key?.toUpperCase?.() ?? '';
}

/** Accent badge colour for pills/counters */
export function getModuleAccentBadge(key) {
  return MODULE_COLORS[key]?.accentBadge ?? MODULE_COLORS.warehouse.accentBadge;
}
